import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Eye, EyeOff, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/collections`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCollections(data);
      }
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisibility = async (collectionId, isPublic) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/collections/${collectionId}/visibility?is_public=${!isPublic}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        fetchCollections();
      }
    } catch (error) {
      console.error('Failed to update visibility:', error);
    }
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/collections/${collectionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchCollections();
      }
    } catch (error) {
      console.error('Failed to delete collection:', error);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Collections</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage user collections</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Name</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Owner</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Items</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Likes</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Views</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Visibility</th>
                  <th className="text-left py-3 px-4 text-gray-600 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">Loading...</td>
                  </tr>
                ) : collections.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">No collections found</td>
                  </tr>
                ) : (
                  collections.map((collection) => (
                    <tr key={collection.id} className="border-t dark:border-gray-700">
                      <td className="py-3 px-4">
                        <span className="font-medium text-gray-900 dark:text-white">{collection.name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-gray-900 dark:text-white">{collection.user?.name}</div>
                        <div className="text-sm text-gray-500">{collection.user?.email}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{collection.items?.length || 0}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{collection.likes || 0}</td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{collection.views || 0}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${
                          collection.is_public
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {collection.is_public ? 'Public' : 'Private'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleToggleVisibility(collection.id, collection.is_public)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title={collection.is_public ? 'Make Private' : 'Make Public'}
                          >
                            {collection.is_public ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleDelete(collection.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Collections;